import React from 'react';
import { useSplitReveal } from '../lib/useSplitReveal';
import { useSteppedSequence } from '../lib/useSteppedSequence';

const STEPS = [
  {
    title: 'Upload a photo.',
    body: 'One clear, well-lit shot of your face is all the model needs to start.',
  },
  {
    title: 'Get your palette.',
    body: 'Your undertone, your contrast level, and the shades that hold up against both.',
  },
  {
    title: 'Shop your shop.',
    body: 'The marketplace restocks itself with items that match your profile and your size.',
  },
  {
    title: 'Plan the week.',
    body: 'Save outfits and lay out what you are wearing before the morning you need it.',
  },
] as const;

const HowItWorksSection: React.FC = () => {
  const heading = useSplitReveal<HTMLHeadingElement>({ onScroll: true });
  const { section, stage } = useSteppedSequence<HTMLElement, HTMLOListElement>({
    selector: 'li',
  });

  return (
    <section
      id="how-it-works"
      ref={section}
      className="screen scroll-mt-16 items-stretch md:min-h-[100dvh]"
    >
      <div className="shell flex flex-col py-8 lg:py-16">
        {/* One centred column: the title block, then the active step. */}
        <div className="rule flex flex-1 flex-col items-center gap-6 pt-6 text-center md:gap-10 md:justify-start md:pt-[10vh] lg:gap-14">
          <div className="flex flex-col items-center">
            {/*
              The one saturated block on the page. The accent box carries the
              section title, so the steps below can stay quiet type.
            */}
            <h2
              ref={heading}
              className="display split-target bg-accent px-5 py-3 text-[clamp(2rem,8vw,5rem)] font-bold text-accent-ink sm:px-10 sm:py-6"
            >
              How it works.
            </h2>
            <p className="mt-4 max-w-[34ch] text-[14px] leading-relaxed text-muted md:mt-8 md:text-[15px]">
              Four steps, once. After that the store stays tuned to you.
            </p>
          </div>

          {/*
            Ordinary vertical list in the markup. On desktop the sequence hook
            stacks these into one frame and steps through them and each step is
            centred display type; on a phone there is no room for that, so the
            same four items read as a compact numbered list — left aligned, one
            hairline rule apiece — which fits inside a single screen.
          */}
          <ol ref={stage} className="relative flex w-full flex-col items-center">
            {STEPS.map((step, i) => (
              <li
                key={step.title}
                className={`flex w-full items-start gap-4 py-3.5 text-left md:flex-col md:items-center md:gap-5 md:py-0 md:text-center ${
                  i > 0 ? 'border-t border-line md:border-t-0' : ''
                }`}
              >
                {/* Index only exists on a phone: on desktop one step is on
                    screen at a time, so a counter beside it would be noise. */}
                <span
                  aria-hidden="true"
                  className="mt-[3px] shrink-0 text-[12px] font-semibold tabular-nums text-accent md:hidden"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>

                <div className="min-w-0 md:contents">
                  <h3 className="display text-[19px] font-medium text-ink md:text-[clamp(2.25rem,4vw,3.5rem)]">
                    {step.title}
                  </h3>
                  <p className="mt-1 max-w-[46ch] text-[14px] leading-relaxed text-muted md:mt-0 md:text-[17px]">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
